// The boat's equipment (TOM-409, cloud ADR 0312 §8): the engines, gearboxes,
// tanks and batteries Neomaris found in the boat's readings, what it shows and
// leaves out and why, and the owner's answer about each. Open questions come
// first. An answer is the boat's, for every owner; "Not on this boat" leaves
// the instance out of the boards, the pickers and Neo, keeps its data, and
// leaves its alarms running, which the row says.
//
// Each engine, gearbox and battery, and the other gear (generators, chargers,
// inverters), shows the make and model the owner gave, and takes one (cloud
// ADR 0425, TOM-878): Neomaris searches for its manual by it.
//
// Below the equipment, "What Neo knows about this boat" (TOM-610): the boat's
// notebook, which every owner shares and may confirm, correct or forget.

import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { useBoatChrome } from '../components/app/boatChrome';
import { BoatNotebook } from '../components/boat/BoatNotebook';
import {
  answerEquipment,
  answerMakeModel,
  answerVersion,
  equipmentSections,
  gearNoun,
  makeModelText,
  questionText,
  readingsCount,
  statusText,
  type EquipmentEntry,
  type GearEntry,
  type GearMakeModel,
  type PresenceAnswer,
} from '../lib/equipment';
import { formatRelativeShort } from '../lib/deviceSeen';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { useEquipment } from '../lib/useEquipment';

const ANSWER_FAILED =
  'Couldn’t save that answer. Nothing changed. Check the connection and try again.';
const ANSWER_CONFLICT = 'Another owner answered first. The list shows their answer now.';

const MAKE_MODEL_MAX = 60;

/**
 * The make and model of one piece of gear (cloud ADR 0425): the owner's words,
 * or a button to give them. Neomaris searches for the manual by them.
 */
function MakeModel({
  label,
  state,
  busy,
  onSave,
}: {
  label: string;
  state: EquipmentEntry['answers']['makeModel'];
  busy: boolean;
  onSave: (answer: GearMakeModel | null, version: number) => Promise<boolean>;
}) {
  const [editing, setEditing] = useState(false);
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const text = makeModelText(state);
  const version = state?.version ?? 0;

  function start() {
    setMake(state?.answer?.make ?? '');
    setModel(state?.answer?.model ?? '');
    setEditing(true);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    const answer = { make: make.trim(), model: model.trim() };
    if (answer.make === '' || answer.model === '') return;
    if (await onSave(answer, version)) setEditing(false);
  }

  if (!editing) {
    return (
      <p className="equipment-row__meta" data-testid="equipment-make-model">
        {text === null ? 'Make and model not given' : `Make and model: ${text}`}{' '}
        <button
          type="button"
          className="tb-btn tb-btn--sm tb-btn--plain"
          disabled={busy}
          onClick={start}
          aria-label={`${text === null ? 'Add' : 'Change'} the make and model of the ${label.toLowerCase()}`}
        >
          {text === null ? 'Add' : 'Change'}
        </button>
        {text !== null ? (
          <button
            type="button"
            className="tb-btn tb-btn--sm tb-btn--plain"
            disabled={busy}
            onClick={() => void onSave(null, version)}
            aria-label={`Forget the make and model of the ${label.toLowerCase()}`}
          >
            Forget
          </button>
        ) : null}
      </p>
    );
  }
  return (
    <form
      className="equipment-make-model"
      onSubmit={(e) => void submit(e)}
      aria-label={`Make and model of the ${label.toLowerCase()}`}
    >
      <input
        className="tb-input"
        value={make}
        maxLength={MAKE_MODEL_MAX}
        placeholder="Make, e.g. Yanmar"
        aria-label="Make"
        onChange={(e) => setMake(e.target.value)}
      />
      <input
        className="tb-input"
        value={model}
        maxLength={MAKE_MODEL_MAX}
        placeholder="Model, e.g. 4JH45"
        aria-label="Model"
        onChange={(e) => setModel(e.target.value)}
      />
      <button
        type="submit"
        className="tb-btn tb-btn--sm"
        disabled={busy || make.trim() === '' || model.trim() === ''}
      >
        Save
      </button>
      <button
        type="button"
        className="tb-btn tb-btn--sm tb-btn--plain"
        disabled={busy}
        onClick={() => setEditing(false)}
      >
        Cancel
      </button>
    </form>
  );
}

/** The family a question is about, on its row. */
const FAMILY_WORD: Readonly<Record<EquipmentEntry['family'], string>> = {
  engine: 'Engine',
  gearbox: 'Gearbox',
  tank: 'Tank',
  battery: 'Battery',
};

function EquipmentRow({
  entry,
  list,
  boatId,
  asked,
  busy,
  onAnswer,
  onMakeModel,
}: {
  entry: EquipmentEntry;
  list: readonly EquipmentEntry[];
  boatId: string;
  /** In the questions section: the row names its family. */
  asked: boolean;
  busy: boolean;
  onAnswer: (entry: EquipmentEntry, answer: PresenceAnswer | null) => void;
  onMakeModel: (
    instance: string,
    answer: GearMakeModel | null,
    version: number
  ) => Promise<boolean>;
}) {
  const answer = entry.answers.presence?.answer ?? null;
  const question = questionText(entry, list);
  const seen = entry.lastSeen === null ? null : formatRelativeShort(entry.lastSeen);
  const leftOut = entry.state === 'left_out';
  return (
    <li className="equipment-row" data-testid={`equipment-row-${entry.instance}`}>
      <div className="equipment-row__body">
        <div className="equipment-row__name">
          <span className="equipment-row__label">{entry.label}</span>
          {asked ? <span className="tb-chip">{FAMILY_WORD[entry.family]}</span> : null}
          {leftOut ? (
            <span className="tb-chip tb-chip--warn" data-testid="equipment-left-out">
              Left out
            </span>
          ) : null}
        </div>
        {/* An open question says why on its own; a settled row says what holds. */}
        {question !== null ? (
          <p className="equipment-row__question">{question}</p>
        ) : (
          <p className="equipment-row__status">{statusText(entry)}</p>
        )}
        <p className="equipment-row__meta">
          {readingsCount(entry)}
          {seen !== null ? ` · last reported ${seen.toLowerCase()}` : ''}
          {entry.pinnedElsewhere > 0
            ? ` · on ${entry.pinnedElsewhere} ${entry.pinnedElsewhere === 1 ? 'item' : 'items'} pinned by hand`
            : ''}
        </p>
        {leftOut && entry.alarms.length > 0 ? (
          <p className="equipment-row__alarms" data-testid="equipment-alarms">
            Still watched by{' '}
            {entry.alarms.map((alarm, i) => (
              <span key={alarm.id}>
                {i > 0 ? ', ' : ''}
                <Link to={`/dashboard/boats/${boatId}/alarms/${alarm.id}`}>{alarm.title}</Link>
              </span>
            ))}
            . Its alarms keep running until you delete them.
          </p>
        ) : null}
        {entry.answers.makeModel !== undefined ? (
          <MakeModel
            label={entry.label}
            state={entry.answers.makeModel}
            busy={busy}
            onSave={(a, v) => onMakeModel(entry.instance, a, v)}
          />
        ) : null}
      </div>
      <div
        className="equipment-row__actions"
        role="group"
        aria-label={`Is the ${entry.label.toLowerCase()} on this boat?`}
      >
        <button
          type="button"
          className="tb-btn tb-btn--sm tb-btn--gray"
          aria-pressed={answer === 'not_on_boat'}
          disabled={busy || answer === 'not_on_boat'}
          onClick={() => onAnswer(entry, 'not_on_boat')}
        >
          Not on this boat
        </button>
        <button
          type="button"
          className="tb-btn tb-btn--sm tb-btn--gray"
          aria-pressed={answer === 'real'}
          disabled={busy || answer === 'real'}
          onClick={() => onAnswer(entry, 'real')}
        >
          This is real
        </button>
        {answer !== null ? (
          <button
            type="button"
            className="tb-btn tb-btn--sm tb-btn--plain"
            disabled={busy}
            onClick={() => onAnswer(entry, null)}
          >
            Let Neomaris decide
          </button>
        ) : null}
      </div>
    </li>
  );
}

export function Equipment() {
  const { boatId, boat, equipmentRevision } = useBoatChrome();
  const boatName = boat?.display_name ?? 'this boat';
  useDocumentTitle(boat ? `${boat.display_name} — Equipment` : 'Equipment — Neomaris');
  const { load, refresh, apply } = useEquipment(boatId, equipmentRevision);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function onAnswer(entry: EquipmentEntry, answer: PresenceAnswer | null) {
    setBusy(entry.instance);
    setNotice(null);
    const result = await answerEquipment(
      boatId,
      entry.instance,
      answer,
      answerVersion(entry),
      crypto.randomUUID()
    );
    setBusy(null);
    if (result.kind === 'saved') apply(result.entry);
    else setNotice(result.kind === 'conflict' ? ANSWER_CONFLICT : ANSWER_FAILED);
    // An answer can raise or settle another instance's question: read them all.
    refresh();
  }

  async function onMakeModel(
    instance: string,
    answer: GearMakeModel | null,
    version: number
  ): Promise<boolean> {
    setBusy(instance);
    setNotice(null);
    const result = await answerMakeModel(boatId, instance, answer, version, crypto.randomUUID());
    setBusy(null);
    if (result.kind !== 'saved') {
      setNotice(result.kind === 'conflict' ? ANSWER_CONFLICT : ANSWER_FAILED);
    }
    refresh();
    return result.kind === 'saved';
  }

  const instances = load.kind === 'ready' ? load.instances : [];
  const gear: GearEntry[] = load.kind === 'ready' ? load.gear : [];
  const sections = equipmentSections(instances);

  return (
    <section className="equipment-page" data-testid="equipment">
      <div className="page-head page-head--with-action">
        <div>
          <h1>Equipment aboard {boatName}</h1>
          <p className="page-sub">
            The engines, gearboxes, tanks and batteries Neomaris found in {boatName}’s readings. Say
            what is really aboard: your answer is kept for every owner, and what you leave out stays
            off the boards, the pickers and Neo. Its data is kept.
          </p>
        </div>
        <Link className="tb-btn tb-btn--sm tb-btn--gray" to={`/dashboard/boats/${boatId}/devices`}>
          Devices
        </Link>
      </div>

      {notice !== null ? (
        <p className="board-notice" role="status">
          {notice}
        </p>
      ) : null}
      {load.kind === 'loading' ? <p className="muted">Loading equipment…</p> : null}
      {load.kind === 'error' ? (
        <ul className="form-errors">
          <li>Couldn’t load the equipment. Check the connection and try again.</li>
        </ul>
      ) : null}
      {load.kind === 'ready' && instances.length === 0 ? (
        <div className="empty-card">
          <p>No engines, tanks or batteries reported yet.</p>
          <p className="muted">They appear here once the boat sends their readings.</p>
        </div>
      ) : null}

      {sections.map((section) => (
        <section
          key={section.key}
          className="equipment-section"
          aria-labelledby={`equipment-${section.key}`}
          data-testid={`equipment-section-${section.key}`}
        >
          <h2 id={`equipment-${section.key}`} className="tb-section-label">
            {section.heading}
          </h2>
          <ul className="tb-list equipment-list">
            {section.entries.map((entry) => (
              <EquipmentRow
                key={entry.instance}
                entry={entry}
                list={instances}
                boatId={boatId}
                asked={section.key === 'questions'}
                busy={busy !== null}
                onAnswer={(e, a) => void onAnswer(e, a)}
                onMakeModel={onMakeModel}
              />
            ))}
          </ul>
        </section>
      ))}

      {gear.length > 0 ? (
        <section
          className="equipment-section"
          aria-labelledby="equipment-gear"
          data-testid="equipment-section-gear"
        >
          <h2 id="equipment-gear" className="tb-section-label">
            Other gear
          </h2>
          <ul className="tb-list equipment-list">
            {gear.map((g) => (
              <li
                key={g.instance}
                className="equipment-row"
                data-testid={`equipment-gear-${g.instance}`}
              >
                <div className="equipment-row__body">
                  <div className="equipment-row__name">
                    <span className="equipment-row__label">{g.label}</span>
                    <span className="tb-chip">{gearNoun(g.family)}</span>
                  </div>
                  {!g.reporting ? <p className="equipment-row__status">Sends no readings</p> : null}
                  <MakeModel
                    label={g.label}
                    state={g.makeModel}
                    busy={busy !== null}
                    onSave={(a, v) => onMakeModel(g.instance, a, v)}
                  />
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <BoatNotebook boatId={boatId} boatName={boatName} equipment={instances} />
    </section>
  );
}
